import React, { useState, useEffect, useCallback } from "react";
import {
  authenticateWithPin,
  tryAutoLogin,
  registerDevice,
  isKioskAuthenticated,
  hasRegisteredDevice,
} from "../../api/kioskAPI";
import "./KioskLogin.css";

interface KioskLoginProps {
  onLoginSuccess: () => void;
}

type LoginState = "checking" | "pin-entry" | "registering" | "success";

const KioskLogin: React.FC<KioskLoginProps> = ({ onLoginSuccess }) => {
  const [loginState, setLoginState] = useState<LoginState>("checking");
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [deviceName, setDeviceName] = useState("");
  const [showDevicePrompt, setShowDevicePrompt] = useState(false);

  // Try auto-login on mount
  useEffect(() => {
    const attemptAutoLogin = async () => {
      // Check if already authenticated
      if (isKioskAuthenticated()) {
        setLoginState("success");
        onLoginSuccess();
        return;
      }

      // Try device token auto-login
      if (hasRegisteredDevice()) {
        try {
          const result = await tryAutoLogin();
          if (result?.success) {
            setLoginState("success");
            onLoginSuccess();
            return;
          }
        } catch {
          // Device token invalid, continue to PIN entry
        }
      }

      setLoginState("pin-entry");
    };

    attemptAutoLogin();
  }, [onLoginSuccess]);

  const handlePinDigit = useCallback((digit: string) => {
    setError(null);
    setPin((prev) => {
      if (prev.length < 8) {
        return prev + digit;
      }
      return prev;
    });
  }, []);

  const handleBackspace = useCallback(() => {
    setPin((prev) => prev.slice(0, -1));
    setError(null);
  }, []);

  const handleClear = useCallback(() => {
    setPin("");
    setError(null);
  }, []);

  const handleSubmitPin = async () => {
    if (pin.length < 4) {
      setError("PIN must be at least 4 digits");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await authenticateWithPin(pin);
      if (result.success) {
        setPin("");
        // Check if device is already registered
        if (hasRegisteredDevice()) {
          setLoginState("success");
          onLoginSuccess();
        } else {
          // Prompt to register this device
          setShowDevicePrompt(true);
        }
      } else {
        setError(result.message || "Invalid PIN");
        setPin("");
      }
    } catch (err) {
      setError("Authentication failed. Please try again.");
      setPin("");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegisterDevice = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await registerDevice(deviceName || undefined);
      if (result.success) {
        setLoginState("success");
        onLoginSuccess();
      } else {
        setError(result.message || "Failed to register device");
      }
    } catch (err) {
      setError("Failed to register device. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkipRegistration = () => {
    setLoginState("success");
    onLoginSuccess();
  };

  // Keyboard support for PIN entry
  useEffect(() => {
    if (loginState !== "pin-entry" || showDevicePrompt) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key >= "0" && e.key <= "9") {
        handlePinDigit(e.key);
      } else if (e.key === "Backspace") {
        handleBackspace();
      } else if (e.key === "Enter" && pin.length >= 4) {
        handleSubmitPin();
      } else if (e.key === "Escape") {
        handleClear();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [loginState, showDevicePrompt, pin, handlePinDigit, handleBackspace, handleClear]);

  if (loginState === "checking") {
    return (
      <div className="kiosk-login">
        <div className="kiosk-login__loading">
          <div className="kiosk-login__spinner" />
          <p>Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (loginState === "success") {
    return (
      <div className="kiosk-login">
        <div className="kiosk-login__success">
          <div className="kiosk-login__checkmark">&#10003;</div>
          <p>Kiosk Ready</p>
        </div>
      </div>
    );
  }

  return (
    <div className="kiosk-login">
      <div className="kiosk-login__container">
        <div className="kiosk-login__header">
          <h1>Kiosk Setup</h1>
          <p>Staff PIN required to activate kiosk mode</p>
        </div>

        {showDevicePrompt ? (
          <div className="kiosk-login__device-registration">
            <h2>Register This Device?</h2>
            <p>
              Registering this device allows automatic login without entering the PIN each time.
            </p>

            <div className="kiosk-login__device-name">
              <label htmlFor="deviceName">Device Name (optional)</label>
              <input
                id="deviceName"
                type="text"
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                placeholder="e.g., Front Desk iPad"
                disabled={isLoading}
              />
            </div>

            {error && <div className="kiosk-login__error">{error}</div>}

            <div className="kiosk-login__device-actions">
              <button
                className="kiosk-login__btn kiosk-login__btn--primary"
                onClick={handleRegisterDevice}
                disabled={isLoading}
              >
                {isLoading ? "Registering..." : "Register Device"}
              </button>
              <button
                className="kiosk-login__btn kiosk-login__btn--secondary"
                onClick={handleSkipRegistration}
                disabled={isLoading}
              >
                Skip for Now
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="kiosk-login__pin-display">
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className={`kiosk-login__pin-dot ${
                    i < pin.length ? "kiosk-login__pin-dot--filled" : ""
                  }`}
                />
              ))}
            </div>

            {error && <div className="kiosk-login__error">{error}</div>}

            <div className="kiosk-login__keypad">
              {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((digit) => (
                <button
                  key={digit}
                  className="kiosk-login__key"
                  onClick={() => handlePinDigit(digit)}
                  disabled={isLoading}
                >
                  {digit}
                </button>
              ))}
              <button
                className="kiosk-login__key kiosk-login__key--action"
                onClick={handleClear}
                disabled={isLoading}
              >
                Clear
              </button>
              <button
                className="kiosk-login__key"
                onClick={() => handlePinDigit("0")}
                disabled={isLoading}
              >
                0
              </button>
              <button
                className="kiosk-login__key kiosk-login__key--action"
                onClick={handleBackspace}
                disabled={isLoading}
              >
                &#9003;
              </button>
            </div>

            <button
              className="kiosk-login__submit"
              onClick={handleSubmitPin}
              disabled={pin.length < 4 || isLoading}
            >
              {isLoading ? "Verifying..." : "Enter"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default KioskLogin;
